// @ts-nocheck
// EOB attach-vs-create stage (create-claim-from-pdf, Phase 6). After EOB review
// the user chooses to ATTACH the denial onto an existing claim (manual pick from
// listClaims) or CREATE a new claim (the Phase-5 path). No mock data — the picker
// is backed by the real listClaims endpoint. Debounce/portal pattern mirrors
// shared/PatientPicker.js.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { listClaims } from '../../../api/claimsClient';
import { Section } from '../shared';

const SEARCH_DEBOUNCE_MS = 250;

function claimPatientName(claim) {
  const insured = claim.insured_snapshot || {};
  const first = insured.first_name || claim.patient_first_name || '';
  const last = insured.last_name || claim.patient_last_name || '';
  return `${first} ${last}`.trim();
}

function claimPayer(claim) {
  return (claim.payer_snapshot && claim.payer_snapshot.name) || claim.payer_name || '';
}

function claimDos(claim) {
  const lines = claim.service_lines || [];
  if (lines.length && lines[0].service_date_from) return lines[0].service_date_from;
  return claim.service_date_from || '';
}

function claimStatus(claim) {
  return claim.draft_status || claim.status || '';
}

function claimTotal(claim) {
  return claim.total_charge != null ? claim.total_charge : '';
}

/** Debounced claim picker backed by listClaims. Mirrors PatientPicker. */
function ClaimPicker({ value, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const inputRef = useRef(null);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const id = setTimeout(() => {
      setSearchError('');
      listClaims(query ? { search: query } : {})
        .then((next) => {
          if (cancelled) return;
          setResults(next || []);
        })
        .catch((err) => {
          if (cancelled) return;
          setResults([]);
          const status = err && err.response && err.response.status;
          setSearchError(
            status === 401
              ? 'Claim search needs a fresh sign-in.'
              : 'Claim search is temporarily unavailable.',
          );
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    function update() {
      if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target) &&
        !(e.target.closest && e.target.closest('[data-picker-dropdown]'))
      ) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <>
      <div ref={inputRef}>
        <input
          aria-label="Search claims"
          placeholder="Search your claims by patient, payer, or account #"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
        />
      </div>
      {open && rect && createPortal(
        <div
          data-picker-dropdown
          style={{
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            maxHeight: 300,
            overflow: 'auto',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 6,
            boxShadow: 'var(--ab-shadow-md)',
            zIndex: 1400,
          }}
        >
          {searchError && (
            <div style={{ padding: 12, fontSize: 12, color: '#b42318' }}>{searchError}</div>
          )}
          {!searchError && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: '#888' }}>
              {query ? 'No matching claims.' : 'Start typing to search your claims.'}
            </div>
          )}
          {results.map((c) => {
            const id = c.id;
            const selected = id === value;
            const meta = [claimPayer(c), claimDos(c) && `DOS ${claimDos(c)}`, claimStatus(c), claimTotal(c) !== '' && `$${claimTotal(c)}`]
              .filter(Boolean)
              .join(' · ');
            return (
              <button
                key={id}
                type="button"
                onClick={() => { onSelect(c); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px',
                  background: selected ? '#f0f0f0' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {claimPatientName(c) || `Claim ${String(id).slice(0, 8)}`}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {String(id).slice(0, 8)} · {meta}
                </div>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * Attach-vs-create choice for an EOB. The parent owns commit; this component
 * surfaces the mode + the selected claim and reports them up via callbacks.
 *
 * @param {{
 *   mode: 'attach'|'create',
 *   selectedClaim: object|null,
 *   onModeChange: (mode: 'attach'|'create') => void,
 *   onSelectClaim: (claim: object|null) => void,
 * }} props
 */
export default function EOBAttach({ mode, selectedClaim, onModeChange, onSelectClaim }) {
  return (
    <Section icon="link" title="Attach this EOB or create a new claim">
      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button
          type="button"
          onClick={() => onModeChange('attach')}
          style={{ flex: 1, padding: 16, borderRadius: 8, border: mode === 'attach' ? '2px solid #111' : '1px solid #ccc', background: '#fff', textAlign: 'left', cursor: 'pointer' }}
        >
          <div style={{ fontWeight: 600 }}>Attach to an existing claim</div>
          <div style={{ color: '#666', fontSize: 13 }}>Write the denial onto a claim you already have.</div>
        </button>
        <button
          type="button"
          onClick={() => onModeChange('create')}
          style={{ flex: 1, padding: 16, borderRadius: 8, border: mode === 'create' ? '2px solid #111' : '1px solid #ccc', background: '#fff', textAlign: 'left', cursor: 'pointer' }}
        >
          <div style={{ fontWeight: 600 }}>Create a new claim</div>
          <div style={{ color: '#666', fontSize: 13 }}>Build a new claim from this EOB.</div>
        </button>
      </div>

      {mode === 'attach' && (
        <div style={{ marginTop: 16 }}>
          <ClaimPicker
            value={selectedClaim ? selectedClaim.id : null}
            onSelect={onSelectClaim}
          />
          {selectedClaim && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#444' }} data-testid="selected-claim">
              Selected: {claimPatientName(selectedClaim) || String(selectedClaim.id).slice(0, 8)}
              {claimPayer(selectedClaim) ? ` · ${claimPayer(selectedClaim)}` : ''}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
